import { createContext, createElement, useCallback, useContext, useMemo, type ReactNode } from "react"

import type { AdminI18n, SharedProps } from "@/types/session"

export type I18nOptions = Record<string, string | number | boolean | null | undefined> & {
  defaultValue?: string
}

export type TranslateFn = (key: string, options?: I18nOptions) => string

export type I18nValue = {
  availableLocales: SharedProps["available_locales"]
  locale: SharedProps["current_locale"]
  t: TranslateFn
}

const I18nContext = createContext<I18nValue | null>(null)

export function InertiaI18nProvider({
  children,
  page,
}: {
  children: ReactNode
  page: Pick<SharedProps, "admin_i18n" | "current_locale" | "available_locales">
}) {
  const { admin_i18n, current_locale, available_locales } = page
  const t = useCallback<TranslateFn>(
    (key, options) => translate(admin_i18n, key, options),
    [admin_i18n]
  )
  const value = useMemo<I18nValue>(
    () => ({ availableLocales: available_locales, locale: current_locale, t }),
    [available_locales, current_locale, t]
  )

  return createElement(I18nContext.Provider, { value }, children)
}

export function DetachedI18nProvider({
  children,
  value,
}: {
  children: ReactNode
  value: I18nValue
}) {
  return createElement(I18nContext.Provider, { value }, children)
}

const lookup = (translations: Record<string, unknown> | undefined, key: string): unknown =>
  key.split(".").reduce<unknown>((node, part) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return undefined

    return (node as Record<string, unknown>)[part]
  }, translations)

const interpolate = (value: string, options: I18nOptions = {}) =>
  value.replace(/%\{(\w+)\}/g, (match, name) => {
    const replacement = options[name]
    return replacement === null || replacement === undefined ? match : String(replacement)
  })

export const translate = (
  adminI18n: AdminI18n | undefined,
  key: string,
  options: I18nOptions = {}
) => {
  const value = lookup(adminI18n?.translations, key)

  if (typeof value === "string") return interpolate(value, options)

  if (value && typeof value === "object" && !Array.isArray(value) && "count" in options) {
    const pluralKey = Number(options.count) === 1 ? "one" : "other"
    const pluralValue = (value as Record<string, unknown>)[pluralKey]

    if (typeof pluralValue === "string") return interpolate(pluralValue, options)
  }

  return options.defaultValue ?? key
}

export function useI18n() {
  const value = useContext(I18nContext)
  if (!value) throw new Error("useI18n must be used within an i18n provider")
  return value
}
